import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Persons } from '~/shared/data-contract/person';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

type JobRoleOrJobLevel = {
    readonly id: string;
    readonly architectureId: string;
    readonly type: 'level' | 'role';
};

export type UseAssignableReviewersParams = {
    readonly jobRoleOrJobLevel: JobRoleOrJobLevel;
    readonly searchTerm?: string;
};

export type UseAssignableReviewersResult = UseQueryResult<Persons, Error>;

export const useAssignableReviewers = ({
    jobRoleOrJobLevel,
    searchTerm = '',
}: UseAssignableReviewersParams): UseAssignableReviewersResult => {
    const { fetchAssignableReviewers } = useApplicationFetchers();

    const { id, architectureId, type } = jobRoleOrJobLevel;

    return useQuery({
        queryFn: async () => {
            const result = await fetchAssignableReviewers({
                jobRoleOrJobLevel: { id, architectureId, type },
                searchTerm,
            });
            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        queryKey:
            type === 'role'
                ? jobArchitectureKeys.assignableRoleReviewers(architectureId, id, searchTerm)
                : jobArchitectureKeys.assignableLevelReviewers(architectureId, id, searchTerm),
        throwOnError: false,
    });
};
